import type React from "react";
import type { KYCFormData } from "../../types";
import { FormSection } from "../form/FormSection";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface Step6Props {
	formData: KYCFormData;
	updateFormData: (updates: Partial<KYCFormData>) => void;
}

interface ContactFieldProps {
	id: string;
	label: string;
	type: "text" | "email" | "tel";
	value: string;
	onChange: (value: string) => void;
	required?: boolean;
}

function ContactField({
	id,
	label,
	type,
	value,
	onChange,
	required = true,
}: ContactFieldProps) {
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id}>
				{label}
				{required && (
					<span aria-hidden="true" className="ml-0.5 text-(--color-error-500)">
						*
					</span>
				)}
			</Label>
			<Input
				id={id}
				type={type}
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
		</div>
	);
}

const Step6Contacts: React.FC<Step6Props> = ({ formData, updateFormData }) => {
	return (
		<div className="w-full">
			<FormSection title="Primary Business Contact">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ContactField
						id="primary-business-contact-name"
						label="Name"
						type="text"
						value={formData.primaryBusinessContact.name}
						onChange={(value) =>
							updateFormData({
								primaryBusinessContact: {
									...formData.primaryBusinessContact,
									name: value,
								},
							})
						}
					/>
					<ContactField
						id="primary-business-contact-email"
						label="Email"
						type="email"
						value={formData.primaryBusinessContact.email}
						onChange={(value) =>
							updateFormData({
								primaryBusinessContact: {
									...formData.primaryBusinessContact,
									email: value,
								},
							})
						}
					/>
				</div>
				<ContactField
					id="primary-business-contact-phone"
					label="Phone"
					type="tel"
					value={formData.primaryBusinessContact.phone}
					onChange={(value) =>
						updateFormData({
							primaryBusinessContact: {
								...formData.primaryBusinessContact,
								phone: value,
							},
						})
					}
				/>
			</FormSection>

			<FormSection title="Primary Product/Technical Contact">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ContactField
						id="primary-product-contact-name"
						label="Name"
						type="text"
						value={formData.primaryProductContact.name}
						onChange={(value) =>
							updateFormData({
								primaryProductContact: {
									...formData.primaryProductContact,
									name: value,
								},
							})
						}
					/>
					<ContactField
						id="primary-product-contact-email"
						label="Email"
						type="email"
						value={formData.primaryProductContact.email}
						onChange={(value) =>
							updateFormData({
								primaryProductContact: {
									...formData.primaryProductContact,
									email: value,
								},
							})
						}
					/>
				</div>
				<ContactField
					id="primary-product-contact-phone"
					label="Phone"
					type="tel"
					value={formData.primaryProductContact.phone}
					onChange={(value) =>
						updateFormData({
							primaryProductContact: {
								...formData.primaryProductContact,
								phone: value,
							},
						})
					}
				/>
			</FormSection>

			<FormSection title="Primary Legal Contact">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ContactField
						id="primary-legal-contact-name"
						label="Name"
						type="text"
						value={formData.primaryLegalContact.name}
						onChange={(value) =>
							updateFormData({
								primaryLegalContact: {
									...formData.primaryLegalContact,
									name: value,
								},
							})
						}
					/>
					<ContactField
						id="primary-legal-contact-email"
						label="Email"
						type="email"
						value={formData.primaryLegalContact.email}
						onChange={(value) =>
							updateFormData({
								primaryLegalContact: {
									...formData.primaryLegalContact,
									email: value,
								},
							})
						}
					/>
				</div>
				<ContactField
					id="primary-legal-contact-phone"
					label="Phone"
					type="tel"
					value={formData.primaryLegalContact.phone}
					onChange={(value) =>
						updateFormData({
							primaryLegalContact: {
								...formData.primaryLegalContact,
								phone: value,
							},
						})
					}
				/>
			</FormSection>

			<FormSection title="Primary Signer">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ContactField
						id="primary-signer-name"
						label="Name"
						type="text"
						value={formData.primarySigner.name}
						onChange={(value) =>
							updateFormData({
								primarySigner: {
									...formData.primarySigner,
									name: value,
								},
							})
						}
					/>
					<ContactField
						id="primary-signer-email"
						label="Email"
						type="email"
						value={formData.primarySigner.email}
						onChange={(value) =>
							updateFormData({
								primarySigner: {
									...formData.primarySigner,
									email: value,
								},
							})
						}
					/>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ContactField
						id="primary-signer-phone"
						label="Phone"
						type="tel"
						value={formData.primarySigner.phone}
						onChange={(value) =>
							updateFormData({
								primarySigner: {
									...formData.primarySigner,
									phone: value,
								},
							})
						}
					/>
					<ContactField
						id="primary-signer-role"
						label="Role"
						type="text"
						required={false}
						value={formData.primarySigner.role ?? ""}
						onChange={(value) =>
							updateFormData({
								primarySigner: {
									...formData.primarySigner,
									role: value,
								},
							})
						}
					/>
				</div>
			</FormSection>
		</div>
	);
};

export default Step6Contacts;
